import PhoneMockup from '@/components/PhoneMockup';
import { FEEDBACK_SCREENSHOT, PROTOTYPE_DISCLAIMER } from '@/content/screenshots';

/**
 * "Structured feedback in plain language" section.
 *
 * Frames the Val Dashboard screenshot inside the existing PhoneMockup so it can be
 * swapped via `screenshots.js` without touching this layout. Public copy stays
 * cautious — it does NOT repeat percentages, scores, performance figures or status
 * labels visible inside the screen.
 */
export default function StructuredFeedbackSection() {
  return (
    <section>
      <div className="vl-container py-24 lg:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <p className="mono-label text-vl-violet mb-4">Structured feedback</p>
            <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl leading-[1.05] text-vl-ink">
              Plain-language observations, not a number you can't interpret.
            </h2>
            <div className="mt-7 space-y-5 text-vl-ink/75 leading-relaxed">
              <p>
                Valanti Labs is being designed to translate movement signals into clear, structured observations —
                what appeared to go well, what may deserve more attention, and an honest indication of how reliable
                each piece of feedback is.
              </p>
              <p>
                Where supported, you may also see a record of the sessions you have completed so you can revisit them
                over time. The shape of this record will be finalised in the release version.
              </p>
              <p>
                We are deliberately cautious about scores, percentages and certainty. The goal is for feedback to
                feel useful at home — not to imply precision the camera cannot honestly support.
              </p>
            </div>
          </div>

          <div className="lg:col-span-6 order-1 lg:order-2 relative">
            <div className="absolute -inset-8 bg-gradient-to-bl from-vl-violet/10 via-transparent to-vl-medical/10 blur-3xl pointer-events-none" />
            <div className="relative flex flex-col items-center">
              <PhoneMockup variant="bare" screenshot={FEEDBACK_SCREENSHOT} />
              <p className="mt-6 max-w-[300px] text-[11px] leading-snug text-vl-ink/55 text-center">
                {PROTOTYPE_DISCLAIMER}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
